// ignore-string-externalization

import React, { useContext } from 'react';
import { css, StyledComponent } from 'styled-components';
import { screenMdMax } from '@spotify-internal/encore-web';

const DURATION = '500ms';

/*
For our current animations we want certain text inside our modules to animate
before or after certain other text. To accomplish this we added a constant
that maps an order descriptor (FIRST, SECOND, etc.) to the number of milliseconds
to delay by. This allows the user to have an easier time reading the code and
understanding what is happening. Delays are typically set to be 250ms apart. This
can be customized if needed by passing a value other than DELAYS.X
*/
export const DELAYS = {
  FIRST: 0,
  SECOND: 250,
  THIRD: 500,
  FOURTH: 750,
  FIFTH: 1000,
  SIXTH: 1250,
  SEVENTH: 1500,
  EIGHTH: 1750,
  NINTH: 2000,
  TENTH: 2250,
};

export const Crossfade = css<{ active: boolean }>`
  /* cross-fade */
  opacity: ${({ active }) => (active ? 1 : 0)};
  z-index: ${({ active }) => (active ? 1 : 'unset')};
  transition: visibility 0.75s, opacity 0.75s linear, z-index 0s 0.75s,
    color 0s 0.75s;
`;

interface IDictionary<TValue> {
  [key: string]: TValue;
}

/*
The current list of customized thresholds for the customThreshold function below
 */
const customThresholds: IDictionary<number> = {
  featuredContent2Up: 0.4,
  featuredContent3Up: 0.4,
  groupTestimonial: 0.6,
  editorialGrid: 0.6,
  relatedLinksModule: 0.5,
  singleTestimonial: 0.6,
};

/*
We have some modules that are larger vertically than others. Due to the way some of these are
layed out we have a large amount of space at the top for an image. The way that intersection
observer works is to say something is on screen once the "threshold" has been met. For example,
a threshold of .4 means to return true that the module is on the screen once it's scrolled no less
than 40 of the module on the screen. We use this to allow us to only trigger animations once the
animatable text is on the screen not just the module itself. We have a default of .25 set.
 */
export const customThreshold = (contentType: string) => {
  if (contentType in customThresholds) {
    return customThresholds[contentType];
  }
  return 0.25;
};

/*
Very simple function that returns the css template to enable a fade in effect. Opacity starts at 0
and goes to 1.
 */
const applyKeyframes = css`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

/*
This function is called when it has been determined that a given module needs to animate. This
sets the text areas to opacity 0 but only if the module is on screen, animatable, and has not yet
animated.
 */
const applyStartState = (props: { animationContext: AnimationContextType }) =>
  !props.animationContext?.isOnScreen &&
  props.animationContext?.moduleAnimatable &&
  props.animationContext?.hasAnimated < 1
    ? `opacity: 0;`
    : '';

/*
This is the function that returns the css that actually sets up the animation duration and custom
delays.
 */
const applyAnimation = (
  delay: number,
  props: { animationContext?: AnimationContextType },
) =>
  props.animationContext?.isOnScreen &&
  props.animationContext?.moduleAnimatable &&
  props.animationContext?.hasAnimated < 2
    ? `animation: ${DURATION} linear ${delay}ms 1 both fadeIn;`
    : '';

/*
get animation text for staggered text animations in modules
The hook uses IntersectionObserver to determine if a given ref (attached to the module) is on the
screen and takes into consideration the custom thresholds.
 */
function useOnScreen(ref: any, rootMargin = '0px', threshold = 0.25) {
  const [isIntersecting, setIntersecting] = React.useState(false);

  React.useEffect(() => {
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIntersecting(entry.isIntersecting);
        },
        {
          rootMargin,
          threshold,
        },
      );
      if (ref?.current) {
        observer.observe(ref.current);
      }
      return () => {
        if (ref?.current) {
          observer.unobserve(ref.current);
        }
      };
    }
    return () => {};
  }, []);
  return isIntersecting;
}

export type AnimationContextType = {
  isOnScreen: boolean;
  moduleAnimatable: boolean;
  hasAnimated: number;
};

/*
This is a React Context object that we use to hold the values that are referenced in applyStartState
and applyAnimation. As long as the context is sent to the module as a prop and passed as a prop to
the styled component we are able to use the animations functions and tie all of our animations bits
together.
 */
export const AnimationContext = React.createContext<AnimationContextType>({
  isOnScreen: false,
  moduleAnimatable: false,
  hasAnimated: 0,
});

/*
This hook is what sets up the animationContext and ref for use by the modules. It creates the ref
needed to enable the useOnScreen hook, creates the isOnScreen const that tells us when the ref
is on the screen, and checks whether the module should animate and if it's in the list of animatable
modules.
 */
export function useAnimations(
  contentType: string,
  shouldAnimate: boolean,
  threshold: number,
) {
  const animationRef = React.createRef();
  const [hasAnimated, setHasAnimated] = React.useState(0);
  const isOnScreen = useOnScreen(animationRef, '0px', threshold);
  const moduleAnimatable = shouldAnimate && isAnimatable(contentType);

  React.useEffect(() => {
    if (isOnScreen) {
      setHasAnimated(hasAnimated + 1);
    }
  }, [isOnScreen]);

  const animationContext: AnimationContextType = {
    isOnScreen,
    moduleAnimatable,
    hasAnimated,
  };
  return { animationContext, animationRef };
}

/*
This function uses apply* functions to set up the actual media query that will be attached to
the proper module text areas/components.
 */
export function fadeInAnimation(delay: number) {
  return css<{
    animationContext: AnimationContextType;
  }>`
    @media (min-width: ${screenMdMax}) {
      ${applyKeyframes};
      ${props => applyStartState(props)};
      ${props => applyAnimation(delay, props)};
    }
  `;
}

/*
This takes any React or Styled Component and attaches the animation context.
 */
export function withAnimationContext(
  Component: React.ComponentType<any> | StyledComponent<any, any>,
) {
  return (props: any) => {
    const animationContext = useContext(AnimationContext);
    return <Component {...props} animationContext={animationContext} />;
  };
}

/*
This is the source of truth for what modules will animate. It is used as a check to prevent
animations from accidentally showing up on non-animatable modules.
 */
const isAnimatable = (mod: string) =>
  [
    'editorialGridModule',
    'heroBodyModule',
    'headerModule',
    'heroBodyCarousel',
    'heroHeaderCarousel',
    'heroNarrativeCarousel',
    'faqLinksModule',
    'groupTestimonial',
    'heroCaseStudyCover',
    'singleTestimonial',
    'singleFeatureModule',
    'pageBreakerModule',
    'featuredContent2Up',
    'featuredContent3up',
    'featuredContentModule',
    'relatedLinksModule',
    'videoModule',
  ].includes(mod);
